<script setup>
import { ref, computed } from "vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import Swal from "sweetalert2";
import { useInscripcionStore } from "@/store/inscripcionStore";
import { inscribirPeleador } from "@/utils/api_evento";
import { deleteFromIndexedDB } from "@/utils/indexedDB";
import { useRouter } from "vue-router";

const emit = defineEmits(["prev-step", "registro-exitoso"]);
const store = useInscripcionStore();
const router = useRouter();

const enviando = ref(false);
const errors = ref({});

const edad = computed(() => {
  if (!store.fechaNacimiento) return null;
  const hoy = new Date();
  const nac = new Date(store.fechaNacimiento);
  let e = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) e--;
  return e;
});

const esJuvenil = computed(() => edad.value !== null && edad.value < 18);

const cintasAdulto = [
  { label: "Blanca", value: "blanca" },
  { label: "Azul", value: "azul" },
  { label: "Morada", value: "morada" },
  { label: "Café", value: "cafe" },
  { label: "Negra", value: "negra" },
];

const cintasJuvenil = [
  { label: "Blanca-Amarilla", value: "blanca_amarilla" },
  { label: "Amarilla", value: "amarilla" },
  { label: "Amarilla-Naranja", value: "amarilla_naranja" },
  { label: "Naranja", value: "naranja" },
  { label: "Naranja-Verde", value: "naranja_verde" },
  { label: "Verde", value: "verde" },
];

const opcionesCintas = computed(() => (esJuvenil.value ? cintasJuvenil : cintasAdulto));

function validar() {
  errors.value = {};
  if (!store.peso_kg) errors.value.peso_kg = true;
  if (!store.cinta) errors.value.cinta = true;
  if (!store.equipo.trim()) errors.value.equipo = true;
  return Object.keys(errors.value).length === 0;
}

async function mostrarExitoYRedirigir() {
  const DELAY = 5; // segundos

  await Swal.fire({
    icon: "success",
    title: "¡Inscripción enviada!",
    html: `
      <p style="margin:0 0 12px;color:#555;font-size:0.95rem;">
        Tu solicitud fue registrada correctamente.<br>
        Recibirás una confirmación por correo electrónico.
      </p>
      <p style="margin:0;color:#888;font-size:0.82rem;">
        Redirigiendo al inicio en <b id="swal-countdown">${DELAY}</b> segundos...
      </p>
    `,
    confirmButtonText: "Ir al inicio ahora",
    confirmButtonColor: "#007bff",
    allowOutsideClick: false,
    allowEscapeKey: false,
    showClass: { popup: "swal2-show" },
    didOpen: () => {
      let segundos = DELAY;
      const el = document.getElementById("swal-countdown");
      const intervalo = setInterval(() => {
        segundos--;
        if (el) el.textContent = segundos;
        if (segundos <= 0) {
          clearInterval(intervalo);
          Swal.close();
        }
      }, 1000);
    },
  });

  // Tanto si el usuario hace clic en el botón como si el timer llega a 0
  router.push("/");
}

async function enviar() {
  if (!validar()) {
    Swal.fire({
      icon: "warning",
      title: "Campos incompletos",
      text: "Completa los campos requeridos antes de enviar.",
      confirmButtonColor: "#007bff",
    });
    return;
  }

  enviando.value = true;

  try {
    const formData = new FormData();

    formData.append("nombre", store.nombre);
    formData.append("apellido", store.apellido);
    formData.append("email", store.email);
    formData.append("telefono", store.telefono);
    formData.append("fecha_nacimiento", store.fechaNacimiento);
    formData.append("genero", store.genero);
    formData.append("nacionalidad", store.nacionalidad);
    formData.append("direccion", store.direccion || "");
    formData.append("redes_sociales", JSON.stringify(store.redes_sociales));

    if (store.foto) formData.append("foto", store.foto);

    formData.append("peso_kg", store.peso_kg);
    formData.append("cinta", store.cinta);
    formData.append("equipo", store.equipo);
    formData.append("trayectoria", store.trayectoria || "");

    await inscribirPeleador(formData);

    await deleteFromIndexedDB("peleadores_evento", "fotoPerfil");
    store.resetFormulario();
    emit("registro-exitoso");

    await mostrarExitoYRedirigir();
  } catch (error) {
    const msg = error?.response?.data;
    const detalle =
      typeof msg === "object"
        ? Object.values(msg).flat().join(" ")
        : "Ocurrió un error al enviar tu solicitud.";

    Swal.fire({
      icon: "error",
      title: "Error al registrar",
      text: detalle,
      confirmButtonColor: "#007bff",
    });
  } finally {
    enviando.value = false;
  }
}
</script>

<template>
  <form class="step-container" @submit.prevent="enviar">
    <!-- Peso + Equipo -->
    <div class="form-row-2">
      <div class="field-group">
        <label class="field-label">Peso (kg) <span class="req">*</span></label>
        <input
          type="number"
          step="0.1"
          min="30"
          max="200"
          class="field-input"
          :class="{ 'field-error': errors.peso_kg }"
          v-model="store.peso_kg"
          placeholder="Ej. 70.5"
        />
        <span v-if="errors.peso_kg" class="error-msg">Requerido</span>
      </div>
      <div class="field-group">
        <label class="field-label">Academia / Equipo <span class="req">*</span></label>
        <input
          type="text"
          class="field-input"
          :class="{ 'field-error': errors.equipo }"
          v-model="store.equipo"
          placeholder="Ej. Gracie Barra MTY"
        />
        <span v-if="errors.equipo" class="error-msg">Requerido</span>
      </div>
    </div>

    <!-- Cinta -->
    <div class="field-group mb-14">
      <label class="field-label">
        Grado de cinta <span class="req">*</span>
        <span v-if="esJuvenil" class="badge-juvenil">Juvenil</span>
      </label>
      <v-select
        v-model="store.cinta"
        :options="opcionesCintas"
        :reduce="(o) => o.value"
        label="label"
        placeholder="Selecciona tu grado"
        :class="{ 'vs-error': errors.cinta }"
      />
      <span v-if="errors.cinta" class="error-msg">Requerido</span>
    </div>

    <!-- Trayectoria -->
    <div class="field-group mb-14">
      <label class="field-label">
        Trayectoria deportiva
        <span class="field-opt">(opcional)</span>
      </label>
      <textarea
        class="field-input"
        v-model="store.trayectoria"
        rows="3"
        placeholder="Cuéntanos sobre tu experiencia en BJJ, torneos, logros..."
      ></textarea>
    </div>

    <!-- Navegación -->
    <div class="nav-row">
      <button type="button" class="btn-nav btn-secondary-nav" @click="$emit('prev-step')">
        <i class="bi bi-arrow-left"></i> Atrás
      </button>
      <button type="submit" class="btn-nav btn-primary-nav" :disabled="enviando">
        <span v-if="enviando"> <span class="spinner-sm"></span> Enviando... </span>
        <span v-else> Enviar inscripción <i class="bi bi-send-fill"></i> </span>
      </button>
    </div>
  </form>
</template>

<style scoped>
.step-container {
  padding: 0.5rem 0;
}
.mb-14 {
  margin-bottom: 14px;
}
.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}
.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #444;
  display: flex;
  align-items: center;
  gap: 6px;
}
.field-opt {
  font-weight: 400;
  color: #aaa;
  font-size: 0.75rem;
}
.req {
  color: #dc3545;
}
.field-input {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #333;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
}
.field-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
}
.field-error {
  border-color: #dc3545 !important;
}
.error-msg {
  font-size: 0.71rem;
  color: #dc3545;
}

.badge-juvenil {
  font-size: 0.65rem;
  font-weight: 700;
  background: #e8f3ff;
  color: #007bff;
  padding: 1px 6px;
  border-radius: 20px;
  border: 1px solid #c8e0ff;
}

:deep(.vs-error .vs__dropdown-toggle) {
  border-color: #dc3545 !important;
}

.spinner-sm {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  animation: giro 0.8s linear infinite;
  vertical-align: middle;
  margin-right: 4px;
}
@keyframes giro {
  to {
    transform: rotate(360deg);
  }
}

.nav-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.btn-nav {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.2s;
}
.btn-primary-nav {
  background: #007bff;
  color: #fff;
}
.btn-primary-nav:hover:not(:disabled) {
  background: #0060df;
}
.btn-primary-nav:disabled {
  background: #b0c8f0;
  cursor: not-allowed;
}
.btn-secondary-nav {
  background: #f0f0f0;
  color: #555;
}
.btn-secondary-nav:hover {
  background: #e0e0e0;
}

@media (max-width: 600px) {
  .form-row-2 {
    grid-template-columns: 1fr;
  }
}
</style>
